/* ==========================================================================================
   Commercial Equipment Online 2026 (theme forked from the earlier 2026 builds) · ce-product.js · the product page (sections/ce-main-product.liquid). Product builder.
   No dependencies. Deferred. Binds every [data-bop] section once; re-binds on shopify:section:load and lets
   go on :unload. Under prefers-reduced-motion every move is instant.

   What it does
   - Options: finds the chosen variant in the section's own JSON (no page load) and brings everything in step:
     the form's id, the address (?variant=), price / was-price / saving, SKU, stock, Add to cart, PayPal,
     the Stock Notification form, Afterpay and SilverChef amounts, Zip's amount, the sold-out marks on the
     other options, the gallery photo, the buy bar. Event: ce:variant:change { variant } on the section.
   - Gallery: a scroll-snap row (works without this file). Thumbnails, arrows, dots, the counter, arrow keys,
     YouTube posters that become the player, videos that pause when swiped away, and the full-screen viewer
     (a <dialog>) with tap / click to zoom and drag to look around.
   - Buy bar (phones): shows once the main Add to cart has scrolled up out of view; its button submits the
     main form, so assets/ce-main.js does the adding (button feedback, cart drawer, header count).
   - Zip: fetches Zip's own widget script once per page, after the page has loaded; our own line shows until
     the widget has drawn.
   ========================================================================================== */
(() => {
  'use strict';
  if (window.CEProduct) return;
  const W = window, D = document;
  const mq = W.matchMedia('(prefers-reduced-motion: reduce)');
  const reduce = () => mq.matches;
  const money = (c) => (W.BO && W.BO.money ? W.BO.money(c) : '$' + ((Number(c) || 0) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','));
  const esc = (t) => String(t == null ? '' : t).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
  const clean = (t) => String(t || '').replace(/_discontinued/gi, '').replace(/_/g, ' ');
  // SilverChef weekly rent, worked out exactly as the live page does it (sections/s_product.liquid there):
  // dollars x .055 x 12 / 52 in floating point, then toFixed(2). The same floats give the same cent
  // ($2,795: 35.475 in binary is just under, so live shows $35.47; a half-up round would say $35.48).
  const silver = (cents) => money(Math.round(parseFloat((((Number(cents) || 0) / 100) * 0.055 * 12 / 52).toFixed(2)) * 100));

  /* ---------------- Zip: one script per page, after load ---------------- */
  const ZIP_SRC = 'https://static.zipmoney.com.au/lib/js/zm-widget-js/dist/zip-widget.min.js';
  const ZIP_MERCHANT = '';   // the store's own Zip merchant id goes here if Zip is ever switched on; empty = Zip's widget never loads.
  let zipAsked = false;
  const zipLoad = () => {
    if (zipAsked || !ZIP_MERCHANT) return;
    zipAsked = true;
    if (!D.querySelector('[data-zm-merchant]')) {
      const m = D.createElement('div');
      m.setAttribute('data-zm-merchant', ZIP_MERCHANT);
      m.setAttribute('data-env', 'production');
      m.hidden = true;
      D.body.appendChild(m);
    }
    const sc = D.createElement('script');
    sc.src = ZIP_SRC;
    sc.async = true;
    D.head.appendChild(sc);
  };
  const zipWatch = (root, offs) => {
    const rows = root.querySelectorAll('[data-bop-zip-widget]');
    if (!rows.length) return;
    rows.forEach((row) => {
      const zm = row.querySelector('[data-zm-widget]');
      if (!zm) return;
      const drawn = () => {
        const ok = zm.children.length > 0 || zm.textContent.trim().length > 0;
        row.classList.toggle('has-widget', ok);
        return ok;
      };
      if (drawn()) return;
      const mo = new MutationObserver(drawn);
      mo.observe(zm, { childList: true, subtree: true, characterData: true });
      offs.push(() => mo.disconnect());
    });
    const go = () => (W.requestIdleCallback ? W.requestIdleCallback(zipLoad, { timeout: 2500 }) : setTimeout(zipLoad, 600));
    if (D.readyState === 'complete') go(); else W.addEventListener('load', go, { once: true });
  };

  /* ---------------- Gallery ---------------- */
  const gallery = (g, offs) => {
    const track = g.querySelector('[data-bop-track]');
    if (!track) return null;
    const slides = Array.prototype.slice.call(track.children);
    const thumbs = Array.prototype.slice.call(g.querySelectorAll('[data-bop-thumb]'));
    const dots = g.querySelector('[data-bop-dots]');
    const count = g.querySelector('[data-bop-count]');
    const prev = g.querySelector('[data-bop-prev]');
    const next = g.querySelector('[data-bop-next]');
    const thumbList = g.querySelector('[data-bop-thumbs]');
    let index = -1, raf = 0;

    // The thumbnail rail ends on a WHOLE thumbnail: as many as fit are shown, and the last one carries "+N"
    // (N = the photos from there on). Its click goes to that photo; the arrows and swipe reach the rest.
    let fitN = 0;                                                   // 0 = every thumbnail shows
    const more = D.createElement('span');
    more.className = 'cep-gal__tmore';
    more.setAttribute('aria-hidden', 'true');
    const markThumbs = () => {
      const cur = fitN && index >= fitN - 1 ? fitN - 1 : index;
      thumbs.forEach((t, k) => { if (k === cur) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current'); });
    };
    const fit = () => {
      if (!thumbList || thumbs.length < 2) return;
      const lis = thumbs.map((t) => t.parentElement);
      more.remove();
      thumbs.forEach((t) => { if (t.ceLabel) t.setAttribute('aria-label', t.ceLabel); });
      lis.forEach((li) => li.classList.remove('is-over'));
      thumbList.classList.remove('is-fit');
      fitN = 0;
      if (thumbList.offsetParent) {
        const cs = W.getComputedStyle(thumbList);
        const col = cs.flexDirection === 'column';
        const gap = parseFloat(col ? cs.rowGap : cs.columnGap) || 0;
        const pad = col ? (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0) : (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
        const size = col ? lis[0].offsetHeight : lis[0].offsetWidth;
        const room = (col ? thumbList.clientHeight : thumbList.clientWidth) - pad;
        const n = size > 0 && room > 0 ? Math.max(1, Math.floor((room + gap) / (size + gap))) : lis.length;
        if (n < lis.length) {
          fitN = n;
          thumbList.classList.add('is-fit');
          lis.forEach((li, k) => li.classList.toggle('is-over', k >= n));
          const last = thumbs[n - 1];
          const rest = thumbs.length - n + 1;
          more.textContent = '+' + rest;
          last.appendChild(more);
          if (!last.ceLabel) last.ceLabel = last.getAttribute('aria-label') || '';
          last.setAttribute('aria-label', last.ceLabel + ', and ' + (rest - 1) + ' more');
        }
      }
      markThumbs();
    };

    const stopMedia = (slide) => {
      if (!slide) return;
      slide.querySelectorAll('video').forEach((v) => { try { v.pause(); } catch (e) { /* fine */ } });
      const f = slide.querySelector('iframe[data-bop-yt-frame]');
      if (f) {                                                      // a YouTube player: back to its poster
        const btn = f.cePoster;
        if (btn) f.replaceWith(btn);
      } else {
        slide.querySelectorAll('iframe').forEach((fr) => { try { fr.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), '*'); } catch (e) { /* fine */ } });
      }
    };
    const setActive = (i) => {
      if (i === index) return;
      const old = slides[index];
      index = i;
      slides.forEach((s, k) => s.classList.toggle('is-current', k === i));
      if (old) stopMedia(old);
      markThumbs();
      if (dots) Array.prototype.forEach.call(dots.children, (d, k) => d.classList.toggle('is-on', k === i));
      if (count) count.textContent = (i + 1) + ' / ' + slides.length;
      if (prev) prev.toggleAttribute('disabled', i === 0);
      if (next) next.toggleAttribute('disabled', i === slides.length - 1);
      const t = thumbs[i];
      if (fitN) return;                                             // the rail fits: nothing to scroll
      if (t && thumbList && thumbList.scrollHeight > thumbList.clientHeight + 2) {
        const top = t.offsetTop - thumbList.clientHeight / 2 + t.offsetHeight / 2;
        thumbList.scrollTo({ top, behavior: reduce() ? 'auto' : 'smooth' });
      } else if (t && thumbList && thumbList.scrollWidth > thumbList.clientWidth + 2) {
        const left = t.offsetLeft - thumbList.clientWidth / 2 + t.offsetWidth / 2;
        thumbList.scrollTo({ left, behavior: reduce() ? 'auto' : 'smooth' });
      }
    };
    const goTo = (i, instant) => {
      const k = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: slides[k].offsetLeft, behavior: instant || reduce() ? 'auto' : 'smooth' });
      setActive(k);
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const w = track.clientWidth || 1;
        setActive(Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / w))));
      });
    };
    track.addEventListener('scroll', onScroll, { passive: true });
    const onThumb = (e) => {
      const a = e.target.closest('[data-bop-thumb]');
      if (!a) return;
      e.preventDefault();
      goTo(parseInt(a.getAttribute('data-bop-thumb'), 10) || 0);
    };
    g.addEventListener('click', onThumb);
    const onPrev = () => goTo(index - 1);
    const onNext = () => goTo(index + 1);
    if (prev) prev.addEventListener('click', onPrev);
    if (next) next.addEventListener('click', onNext);
    const onKey = (e) => {
      if (e.target !== track) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1); }
      else if (e.key === 'Home') { e.preventDefault(); goTo(0); }
      else if (e.key === 'End') { e.preventDefault(); goTo(slides.length - 1); }
    };
    track.addEventListener('keydown', onKey);

    // YouTube in the gallery: the poster button becomes the player (kept, so swiping away restores it)
    const onYt = (e) => {
      const b = e.target.closest('[data-bop-yt]');
      if (!b || !g.contains(b)) return;
      const id = b.getAttribute('data-bop-yt');
      const f = D.createElement('iframe');
      f.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1';
      f.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      f.allowFullscreen = true;
      f.title = (b.getAttribute('aria-label') || 'Video').replace(/^play video:\s*/i, '');
      f.className = 'cep-gal__iframe';
      f.setAttribute('data-bop-yt-frame', '');
      f.cePoster = b;
      b.replaceWith(f);
      f.focus();
    };
    g.addEventListener('click', onYt);

    // Tommy 12-10 (SKOPE's 360° models, e.g. TCE1000N): "Spin in 360°" puts Shopify's own 3D viewer in place of the
    // picture and loads Shopify's viewer controls (model-viewer-ui) only then. Dragging the model turns it (the
    // viewer takes the pointer, so the gallery does not swipe away underneath it).
    const onModel = (e) => {
      const b = e.target.closest('[data-bop-model-load]');
      if (!b || !g.contains(b)) return;
      const box = b.closest('[data-bop-model]');
      const tpl = box && box.querySelector('template[data-bop-model-tpl]');
      if (!tpl) return;
      box.insertBefore(tpl.content.cloneNode(true), b);
      b.remove();
      box.classList.add('is-live');
      const mv = box.querySelector('model-viewer');
      // ☠️ BOTH features: 'model-viewer' is the 3D engine itself (defines <model-viewer>), 'model-viewer-ui' only
      // Shopify's controls round it - loading the UI alone leaves a dead box (measured on the live page 12-10)
      if (mv && W.Shopify && typeof W.Shopify.loadFeatures === 'function') {
        W.Shopify.loadFeatures([{ name: 'model-viewer', version: '1.12' }, { name: 'model-viewer-ui', version: '1.0' }], (errors) => {
          if (errors || !W.Shopify.ModelViewerUI) return;
          try { box.ceUI = new W.Shopify.ModelViewerUI(mv); } catch (err) { /* the bare viewer still works */ }
        });
      }
    };
    g.addEventListener('click', onModel);
    ['pointerdown', 'touchstart'].forEach((t) => g.addEventListener(t, (e) => {
      if (e.target.closest && e.target.closest('model-viewer')) e.stopPropagation();
    }, { passive: true }));
    // "View in your space": Shopify's AR, on phones that can (it hides itself elsewhere)
    const xr = g.querySelector('script[data-bop-xr]');
    if (xr && W.Shopify && typeof W.Shopify.loadFeatures === 'function') {
      W.Shopify.loadFeatures([{ name: 'shopify-xr', version: '1.0', onLoad: (errors) => {
        if (errors) return;
        const setup = () => {
          try { W.ShopifyXR.addModels(JSON.parse(xr.textContent)); W.ShopifyXR.setupXRElements(); } catch (err) { /* no AR */ }
        };
        if (W.ShopifyXR) setup(); else D.addEventListener('shopify_xr_initialized', setup, { once: true });
      } }]);
    }

    // start where the markup says (the chosen option's photo), without an animation
    const start = parseInt(g.getAttribute('data-start'), 10) || 0;
    if (start > 0) requestAnimationFrame(() => goTo(start, true)); else setActive(0);

    // fit the rail now and whenever its box changes (window size, the stage's height)
    if (thumbList && thumbs.length > 1) {
      fit();
      if ('ResizeObserver' in W) {
        let fr = 0;
        const ro = new ResizeObserver(() => { if (!fr) fr = requestAnimationFrame(() => { fr = 0; fit(); }); });
        ro.observe(thumbList);
        offs.push(() => ro.disconnect());
      }
    }

    // the full-screen viewer
    const lb = g.querySelector('[data-bop-lightbox]');
    if (lb) viewer(g, lb, offs, (b) => { const k = slides.findIndex((s) => s.contains(b)); if (k > -1) goTo(k, true); });

    offs.push(() => {
      track.removeEventListener('scroll', onScroll);
      track.removeEventListener('keydown', onKey);
      g.removeEventListener('click', onThumb);
      g.removeEventListener('click', onYt);
      g.removeEventListener('click', onModel);
      if (prev) prev.removeEventListener('click', onPrev);
      if (next) next.removeEventListener('click', onNext);
    });
    return {
      goToMedia(id) {
        const k = slides.findIndex((s) => s.getAttribute('data-media-id') === String(id));
        if (k > -1) goTo(k);
      },
    };
  };

  /* ---------------- Full-screen viewer with zoom ---------------- */
  const viewer = (g, lb, offs, show) => {
    const track = lb.querySelector('[data-bop-lb-track]');
    const slides = Array.prototype.slice.call(track.children);
    const counter = lb.querySelector('[data-bop-lb-count]');
    const prev = lb.querySelector('[data-bop-lb-prev]');
    const next = lb.querySelector('[data-bop-lb-next]');
    let at = 0, opener = null, raf = 0;
    const H = D.documentElement;
    const unzoom = () => slides.forEach((s) => { s.classList.remove('is-zoomed'); const im = s.querySelector('img'); if (im) im.style.transformOrigin = ''; });
    const mark = (i) => {
      at = i;
      if (counter) counter.textContent = (i + 1) + ' / ' + slides.length;
      if (prev) prev.toggleAttribute('disabled', i === 0);
      if (next) next.toggleAttribute('disabled', i === slides.length - 1);
    };
    const go = (i, instant) => {
      const k = Math.max(0, Math.min(slides.length - 1, i));
      unzoom();
      track.scrollTo({ left: slides[k].offsetLeft, behavior: instant || reduce() ? 'auto' : 'smooth' });
      mark(k);
    };
    const open = (i, from) => {
      opener = from || null;
      if (typeof lb.showModal === 'function') lb.showModal(); else lb.setAttribute('open', '');
      H.classList.add('cep-lock');
      // lazy photos in a closed dialog never loaded: ask for this one and its neighbours now
      [i - 1, i, i + 1].forEach((k) => { const im = slides[k] && slides[k].querySelector('img'); if (im) im.loading = 'eager'; });
      requestAnimationFrame(() => go(i, true));
      const x = lb.querySelector('[data-bop-lb-close]');
      if (x) x.focus();
    };
    const close = () => {
      unzoom();
      if (lb.open && typeof lb.close === 'function') lb.close(); else lb.removeAttribute('open');
    };
    const onClose = () => {
      H.classList.remove('cep-lock');
      // phone review 02-10: the page's gallery shows the photo the viewer was on (it went back to the one opened), and
      // the focus goes to that photo's button without scrolling the gallery back
      const b = Array.prototype.find.call(g.querySelectorAll('[data-bop-open="' + at + '"]'), (el) => !lb.contains(el));
      if (b && show) show(b);
      const f = b || opener;
      if (f && f.isConnected) f.focus({ preventScroll: true });
    };
    lb.addEventListener('close', onClose);
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const k = Math.round(track.scrollLeft / (track.clientWidth || 1));
        if (k !== at) { unzoom(); mark(k); const im = slides[k + 1] && slides[k + 1].querySelector('img'); if (im) im.loading = 'eager'; }
      });
    };
    track.addEventListener('scroll', onScroll, { passive: true });

    const onOpen = (e) => {
      const b = e.target.closest('[data-bop-open]');
      if (!b || !g.contains(b) || lb.contains(b)) return;
      open(parseInt(b.getAttribute('data-bop-open'), 10) || 0, b);
    };
    g.addEventListener('click', onOpen);

    // zoom: a tap or click zooms in at that point; moving the pointer (or dragging) looks around; again = out
    let drag = null;
    const aim = (im, x, y) => {
      const r = im.getBoundingClientRect();
      const px = Math.max(0, Math.min(100, ((x - r.left) / r.width) * 100));
      const py = Math.max(0, Math.min(100, ((y - r.top) / r.height) * 100));
      im.style.transformOrigin = px.toFixed(1) + '% ' + py.toFixed(1) + '%';
    };
    const onDown = (e) => {
      const im = e.target.closest('.cep-lb__img');
      if (!im) return;
      drag = { im, x: e.clientX, y: e.clientY, moved: false };
    };
    const onMove = (e) => {
      const s = e.target.closest('.cep-lb__slide');
      if (!s || !s.classList.contains('is-zoomed')) return;
      const im = s.querySelector('img');
      if (e.pointerType === 'mouse') { aim(im, e.clientX, e.clientY); return; }
      if (!drag) return;
      e.preventDefault();
      if (Math.abs(e.clientX - drag.x) + Math.abs(e.clientY - drag.y) > 4) drag.moved = true;
      // a finger drags the photo: the origin moves the other way
      const r = im.getBoundingClientRect();
      const o = (im.style.transformOrigin || '50% 50%').split(' ').map(parseFloat);
      const nx = o[0] - ((e.clientX - drag.x) / r.width) * 100;
      const ny = o[1] - ((e.clientY - drag.y) / r.height) * 100;
      drag.x = e.clientX; drag.y = e.clientY;
      im.style.transformOrigin = Math.max(0, Math.min(100, nx)).toFixed(1) + '% ' + Math.max(0, Math.min(100, ny)).toFixed(1) + '%';
    };
    const onUp = (e) => {
      if (!drag) return;
      const d = drag;
      drag = null;
      if (d.moved) return;
      const s = d.im.closest('.cep-lb__slide');
      const on = !s.classList.contains('is-zoomed');
      unzoom();
      // phone review 02-10: zoomed in, the photo is about 2.4 screens wide: ask for the big file (it kept the 960px one)
      if (on) { s.classList.add('is-zoomed'); aim(d.im, e.clientX, e.clientY); if (d.im.sizes !== '240vw') d.im.sizes = '240vw'; }
    };
    track.addEventListener('pointerdown', onDown);
    track.addEventListener('pointermove', onMove);
    track.addEventListener('pointerup', onUp);
    const onClick = (e) => {
      if (e.target === lb) { close(); return; }                   // the backdrop
      if (e.target.closest('[data-bop-lb-close]')) close();
      else if (e.target.closest('[data-bop-lb-prev]')) go(at - 1);
      else if (e.target.closest('[data-bop-lb-next]')) go(at + 1);
    };
    lb.addEventListener('click', onClick);
    const onKey = (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(at + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(at - 1); }
    };
    lb.addEventListener('keydown', onKey);
    offs.push(() => {
      close();
      H.classList.remove('cep-lock');
      lb.removeEventListener('close', onClose);
      lb.removeEventListener('click', onClick);
      lb.removeEventListener('keydown', onKey);
      track.removeEventListener('scroll', onScroll);
      track.removeEventListener('pointerdown', onDown);
      track.removeEventListener('pointermove', onMove);
      track.removeEventListener('pointerup', onUp);
      g.removeEventListener('click', onOpen);
    });
  };

  /* ---------------- The price, as snippets/ce-price.liquid prints it ---------------- */
  const priceHtml = (v, p) => {
    const wrap = p.closest('[data-bop-price]') || p;
    const saleWord = wrap.getAttribute('data-w-sale') || 'Sale price';
    const regWord = wrap.getAttribute('data-w-reg') || 'Regular price';
    const sale = v.compare_at_price > v.price;
    let h = '<span class="ce-vh">' + esc(sale ? saleWord : regWord) + '</span><span class="ce-price__now">' + esc(money(v.price)) + '</span>';
    if (sale) h += '<span class="ce-price__off"><span class="ce-price__was"><span class="ce-vh">' + esc(regWord) + '</span> <s>' + esc(money(v.compare_at_price)) + '</s></span></span>';
    return h;
  };

  /* ---------------- One product section ---------------- */
  const bind = (root) => {
    if (root.ceBop) return;
    const offs = [];
    root.ceBop = { offs };
    const gal = gallery(root.querySelector('[data-bop-gallery]') || D.createElement('div'), offs);
    zipWatch(root, offs);

    const form = root.querySelector('form[data-bop-form]');
    const buy = root.querySelector('[data-bop-buy]');
    const atc = root.querySelector('[data-bop-atc]');
    const bar = root.querySelector('[data-bop-bar]');
    const barBtn = bar && bar.querySelector('[data-bop-bar-btn]');
    // The buy bar lives on <body>: inside the section it sat in the section's own stacking layer (.ce-section
    // is isolation: isolate), so the sections under it painted over it and a tap hit their text instead.
    if (bar) {
      const sid = root.getAttribute('data-section') || '';
      D.querySelectorAll('body > [data-bop-bar]').forEach((old) => { if (old !== bar && old.getAttribute('data-bop-bar') === sid) old.remove(); });
      D.body.appendChild(bar);
      offs.push(() => bar.remove());
    }
    // SilverChef: the page prints a rounded cent; put the live page's exact figure in.
    root.querySelectorAll('[data-bop-pay]').forEach((pay) => {
      const cents = parseInt(pay.getAttribute('data-bop-price'), 10);
      if (cents >= 0) pay.querySelectorAll('[data-bop-sc-amount]').forEach((el) => { el.textContent = silver(cents); });
    });
    // Bold Upsell: the live page's Add to cart adds straight away (Bold finds no button there to take over).
    // Here Bold clones our button, hides ours and opens its "Would you like to add any Accessories?" window
    // before anything reaches the cart. Put our own button back and drop the clone, every time Bold tries.
    if (form) {
      const unBold = () => {
        form.querySelectorAll('.bold_clone').forEach((cl) => cl.remove());
        form.querySelectorAll('.bold_hidden').forEach((o) => { o.classList.remove('bold_hidden'); o.style.removeProperty('display'); });
      };
      unBold();
      const mo = new MutationObserver(() => { if (form.querySelector('.bold_clone, .bold_hidden')) unBold(); });
      mo.observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      offs.push(() => mo.disconnect());
    }
    const jsonEl = root.querySelector('[data-bop-variants]');
    let variants = [];
    try { variants = jsonEl ? JSON.parse(jsonEl.textContent) : []; } catch (e) { variants = []; }
    let current = null;

    /* --- options --- */
    const optionEls = Array.prototype.slice.call(root.querySelectorAll('[data-bop-option]'));
    const readVals = () => optionEls.map((el) => {
      const sel = el.querySelector('select');
      if (sel) return sel.value;
      const r = el.querySelector('input:checked');
      return r ? r.value : null;
    });
    const find = (vals) => variants.filter((v) => v.options.every((o, i) => o === vals[i]))[0] || null;

    const markAvailability = (vals) => {
      optionEls.forEach((el, i) => {
        const inputs = el.querySelectorAll('input[type="radio"], option');
        inputs.forEach((inp) => {
          const test = vals.slice();
          test[i] = inp.value;
          const ok = variants.some((v) => v.available && v.options.every((o, k) => o === test[k]));
          if (inp.tagName === 'OPTION') {
            const base = inp.getAttribute('data-label') || inp.value;
            inp.textContent = base + (ok ? '' : ' - ' + (root.getAttribute('data-w-sold') || 'Sold out'));
          } else {
            const lab = el.querySelector('label[for="' + inp.id + '"]');
            if (lab) {
              lab.classList.toggle('is-unavailable', !ok);
              const note = lab.querySelector('[data-bop-soldout-note]');
              if (note) note.textContent = ok ? '' : ' (' + (root.getAttribute('data-w-sold') || 'Sold out') + ')';
            }
          }
        });
        const shown = el.querySelector('[data-bop-opt-value]');
        if (shown) {
          const r = el.querySelector('input:checked');
          shown.textContent = r ? (r.getAttribute('data-label') || clean(r.value)) : '';
        }
      });
    };

    const setText = (sel, text) => root.querySelectorAll(sel).forEach((el) => { el.textContent = text; });
    const show = (sel, on) => root.querySelectorAll(sel).forEach((el) => { el.hidden = !on; });

    const apply = (v) => {
      current = v;
      const exists = !!v;
      const can = exists && v.available;
      const idInput = form && form.querySelector('[data-bop-variant-id]');
      if (idInput && exists) { idInput.value = v.id; idInput.dispatchEvent(new Event('change', { bubbles: true })); }

      // price, saving
      if (exists) {
        root.querySelectorAll('[data-bop-price] .ce-price').forEach((p) => {
          p.classList.toggle('ce-price--sale', v.compare_at_price > v.price);
          p.innerHTML = priceHtml(v, p);
        });
        const sale = v.compare_at_price > v.price;
        const amt = sale ? v.compare_at_price - v.price : 0;
        root.querySelectorAll('[data-bop-save]').forEach((el) => {
          el.hidden = !(sale && v.available);                       // no "You save" on what cannot be bought
          const a = el.querySelector('[data-bop-save-amt]'); if (a) a.textContent = money(amt);
          const p = el.querySelector('[data-bop-save-pct]'); if (p) p.textContent = sale ? String(Math.round((amt * 100) / v.compare_at_price)) : '0';
        });
        root.querySelectorAll('[data-bop-badge]').forEach((b) => {
          b.hidden = !sale;
          const p = b.querySelector('[data-bop-badge-pct]'); if (p) p.textContent = sale ? String(Math.round((amt * 100) / v.compare_at_price)) : '0';
        });
        // SKU
        setText('[data-bop-sku]', v.sku || '');
        show('[data-bop-sku-wrap]', !!v.sku);
        // pay later
        root.querySelectorAll('[data-bop-pay]').forEach((pay) => {
          const min = parseFloat(pay.getAttribute('data-bop-ap-min')) || 100;
          const max = parseFloat(pay.getAttribute('data-bop-ap-max')) || 200000;
          const ap = pay.querySelector('[data-bop-ap]');
          if (ap) ap.hidden = !(v.available && v.price >= min && v.price <= max);
          pay.querySelectorAll('[data-bop-ap-amount]').forEach((el) => { el.textContent = money(Math.round(v.price / 4)); });
          pay.querySelectorAll('[data-bop-sc-amount]').forEach((el) => { el.textContent = silver(v.price); });
          pay.querySelectorAll('[data-zm-amount]').forEach((el) => { el.setAttribute('data-zm-amount', (v.price / 100).toFixed(2)); });
        });
        // gallery
        if (gal && v.featured_media) gal.goToMedia(v.featured_media);
        // address
        try {
          const u = new URL(W.location.href);
          u.searchParams.set('variant', v.id);
          W.history.replaceState(W.history.state, '', u.toString());
        } catch (e) { /* fine */ }
      }

      // stock, buttons, notify
      if (buy) {
        const stock = buy.querySelector('[data-bop-stock]');
        if (stock) {
          stock.classList.toggle('ce-stock--out', !can);
          stock.textContent = can ? buy.getAttribute('data-t-in') : (exists ? buy.getAttribute('data-t-out') : buy.getAttribute('data-t-unavail'));
        }
        // "Only N left in <colour>": a tracked option with 0 < N <= the threshold (spare parts: any N). Never 0 or below.
        buy.querySelectorAll('[data-bop-low]').forEach((low) => {
          const max = parseInt(low.getAttribute('data-max'), 10) || 25;
          const part = low.getAttribute('data-part') === '1';
          const n = exists && can && v.tracked ? (parseInt(v.qty, 10) || 0) : 0;
          const on = n > 0 && (part || n <= max);
          low.hidden = !on;
          if (!on) return;
          let tpl = low.getAttribute('data-tpl') || 'Only [count] left in [colour]';
          const col = (v.colour || '').trim();
          if (!col) tpl = tpl.replace(' in [colour]', '');
          const t = low.querySelector('[data-bop-low-text]');
          if (t) t.textContent = tpl.replace('[count]', String(n)).replace('[colour]', col);
        });
        show('[data-bop-can-buy]', can);
        show('[data-bop-dynamic]', can);
        // every copy of the button: the Bold Upsell app hides ours and shows its own clone of it
        root.querySelectorAll('[data-bop-atc]').forEach((b) => {
          b.disabled = !can;
          const t = b.querySelector('[data-bop-atc-text]');
          if (t) t.textContent = can ? buy.getAttribute('data-t-atc') : buy.getAttribute('data-t-sold');
        });
        const notify = buy.querySelector('[data-bop-notify]');
        if (notify && !notify.querySelector('[data-bop-notify-ok]')) notify.hidden = !(exists && !can);
        const body = buy.querySelector('[data-bop-notify-body]');
        if (body && exists) {
          body.value = 'Stock notification request: ' + body.getAttribute('data-base') + (variants.length > 1 ? ' (' + clean(v.title) + ')' : '') + (v.sku ? ' - SKU ' + v.sku : '') + ' - ' + body.getAttribute('data-url') + '?variant=' + v.id;
        }
        const live = buy.querySelector('[data-bop-live]');
        if (live) live.textContent = exists ? clean(v.title) + ', ' + money(v.price) + ', ' + (can ? buy.getAttribute('data-t-in') : buy.getAttribute('data-t-out')) : buy.getAttribute('data-t-unavail');
      }

      // buy bar
      if (bar) {
        bar.querySelectorAll('[data-bop-bar-price]').forEach((el) => { el.textContent = exists ? money(v.price) : ''; });
        const img = bar.querySelector('[data-bop-bar-img]');
        if (img && v && v.thumb) { img.src = v.thumb; img.removeAttribute('srcset'); }
        if (barBtn) {
          const t = barBtn.querySelector('[data-bop-bar-text]');
          if (t) {
            if (!t.getAttribute('data-label')) t.setAttribute('data-label', t.textContent);
            t.textContent = can ? t.getAttribute('data-label') : (buy ? buy.getAttribute('data-t-sold') : 'Sold out');
          }
          barBtn.classList.toggle('is-soldout', !can);
        }
      }
      root.dispatchEvent(new CustomEvent('ce:variant:change', { bubbles: true, detail: { variant: v } }));
    };

    const onOptChange = (e) => {
      if (!e.target.closest('[data-bop-option]')) return;
      const vals = readVals();
      markAvailability(vals);
      apply(find(vals));
    };
    root.addEventListener('change', onOptChange);
    offs.push(() => root.removeEventListener('change', onOptChange));
    // the page may come back from the cache with other boxes ticked than the markup said
    if (variants.length) {
      const vals = readVals();
      const v = find(vals);
      markAvailability(vals);
      const idInput = form && form.querySelector('[data-bop-variant-id]');
      if (v && idInput && String(v.id) !== idInput.value) apply(v);
      else current = v;
    }

    /* --- buy bar (phones) --- */
    if (bar && atc) {
      let shown = false;
      const set = (on) => {
        if (on === shown) return;
        shown = on;
        bar.classList.toggle('is-on', on);
        bar.setAttribute('aria-hidden', on ? 'false' : 'true');
        if (on) bar.removeAttribute('inert'); else bar.setAttribute('inert', '');
        D.documentElement.classList.toggle('cep-bar-on', on);
      };
      const target = root.querySelector('[data-bop-buy]') || atc;
      // A scroll check, not an IntersectionObserver: a jump straight past the buy box (the stars link to the
      // reviews, a fast fling) never crosses the viewport edge, so an observer would never fire.
      let tick = 0;
      const check = () => { tick = 0; set(target.getBoundingClientRect().bottom < 0); };
      const onScroll = () => { if (!tick) tick = W.requestAnimationFrame(check); };
      W.addEventListener('scroll', onScroll, { passive: true });
      W.addEventListener('resize', onScroll, { passive: true });
      check();
      const io = { disconnect: () => { W.removeEventListener('scroll', onScroll); W.removeEventListener('resize', onScroll); if (tick) W.cancelAnimationFrame(tick); } };
      const onBar = () => {
        if (!form) return;
        if (atc.disabled || !(current ? current.available : true)) { target.scrollIntoView({ behavior: reduce() ? 'auto' : 'smooth', block: 'center' }); return; }
        barBtn.classList.remove('is-added');
        barBtn.classList.add('is-loading');
        if (typeof form.requestSubmit === 'function') form.requestSubmit(); else atc.click();
      };
      const onAdded = () => {
        barBtn.classList.remove('is-loading');
        barBtn.classList.add('is-added');
        clearTimeout(barBtn.boT);
        barBtn.boT = setTimeout(() => barBtn.classList.remove('is-added'), 2400);
      };
      const onErr = () => { barBtn.classList.remove('is-loading'); target.scrollIntoView({ behavior: reduce() ? 'auto' : 'smooth', block: 'center' }); };
      barBtn.addEventListener('click', onBar);
      form.addEventListener('ce:cart:added', onAdded);
      form.addEventListener('ce:cart:error', onErr);
      offs.push(() => {
        io.disconnect();
        barBtn.removeEventListener('click', onBar);
        form.removeEventListener('ce:cart:added', onAdded);
        form.removeEventListener('ce:cart:error', onErr);
        D.documentElement.classList.remove('cep-bar-on');
      });
    }

    /* --- Add to cart: the label says "Added to cart" while ce-main.js shows the green state --- */
    if (form) {
      const onAddedMain = () => {
        const l = buy && buy.querySelector('[data-bop-live]');
        if (l) l.textContent = (atc && atc.querySelector('.cep-atc__done') ? atc.querySelector('.cep-atc__done').textContent : 'Added to cart');
        // ce-main.js marks the form's first button; a Bold clone the shopper actually sees gets the same tick
        form.querySelectorAll('[data-bop-atc]').forEach((b) => {
          if (b.classList.contains('is-added')) return;
          b.classList.add('is-added');
          clearTimeout(b.bopT);
          b.bopT = setTimeout(() => b.classList.remove('is-added'), 2400);
        });
      };
      form.addEventListener('ce:cart:added', onAddedMain);
      offs.push(() => form.removeEventListener('ce:cart:added', onAddedMain));
    }
  };

  const release = (root) => {
    if (!root.ceBop) return;
    root.ceBop.offs.forEach((f) => { try { f(); } catch (e) { /* gone */ } });
    delete root.ceBop;
  };
  const init = (scope) => {
    const r = scope && scope.querySelectorAll ? scope : D;
    r.querySelectorAll('[data-bop]').forEach(bind);
    if (r.matches && r.matches('[data-bop]')) bind(r);
  };
  W.CEProduct = { init, release };
  // The sale engine (sale-home builder) sets <html data-ce-sale="<event-id>"> and fires ce:sale:change. When its
  // detail names the event ({ title } or { name }), the badges marked [data-ce-sale-title] say that name;
  // otherwise they keep their own words (data-default). [data-ce-sale-only] show/hide is pure CSS.
  const saleTitle = (e) => {
    const d = (e && e.detail) || {};
    const live = D.documentElement.hasAttribute('data-ce-sale');
    const name = live ? String(d.title || d.name || (d.event && (d.event.title || d.event.name)) || '').trim() : '';
    D.querySelectorAll('[data-ce-sale-title]').forEach((el) => {
      el.textContent = name ? (el.getAttribute('data-ce-sale-tpl') || '[event]').replace('[event]', name) : (el.getAttribute('data-default') || el.textContent);
    });
  };
  D.addEventListener('ce:sale:change', saleTitle);
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', () => init(D)); else init(D);
  D.addEventListener('shopify:section:load', (e) => init(e.target));
  D.addEventListener('shopify:section:unload', (e) => { e.target.querySelectorAll('[data-bop]').forEach(release); });
})();
/* pdp 08-10-2026: the run-out colour line ([data-cep-runout], sections/ce-main-product.liquid, Options block)
   follows the chosen colour: shown only when the variant's colour is in the block's run-out list. */
(() => {
  'use strict';
  const handle = (t) => String(t || '').toLowerCase().replace(/['"]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  document.addEventListener('ce:variant:change', (e) => {
    const v = e.detail && e.detail.variant;
    const root = e.target && e.target.closest ? (e.target.closest('[data-bop]') || e.target) : document;
    if (!v || !root.querySelectorAll) return;
    root.querySelectorAll('[data-cep-runout]').forEach((el) => {
      const names = el.getAttribute('data-names') || '|';
      const col = String(v.colour || '').trim();
      const on = !!col && names.indexOf('|' + handle(col) + '|') > -1;
      if (on) el.textContent = (el.getAttribute('data-tpl') || '[colour]').replace('[colour]', col);
      el.hidden = !on;
    });
  });
})();
